/* ===== world.js — 세계 생성 / 충돌 / 조명 ===== */
import { factory as Factory } from './ctx.js';
import { clamp, dist, inv, lerp } from '../engine/core/math.js';
import { makeNoise1D, makeNoise2D } from '../engine/core/noise.js';
import { RNG } from '../engine/core/rng.js';
import { rleDecode, rleEncode } from '../engine/save/rle.js';
import { LightField } from '../engine/tilemap/lightfield.js';
import { TileMap } from '../engine/tilemap/tilemap.js';
import { SHIFT, applyWorldSize, dimsOf } from './size.js';
import { T, TILE_DEF } from './data.js';
import { OBJ_SIZE } from './data/items.js';
import { FLUID_FLOW, FLUID_KIND } from './data/materials.js';
import { RUIN_SPEC } from './data/ruins.js';

export const TS = 22;              // 타일 픽셀 크기
export const CAVE_GW = 60, CAVE_GH = 55; // 동굴 갈래 구역 한 칸의 크기(buildCaveZones)
/* 이보다 작고 고립된(지상과 안 통하는) 공동은 동굴로 치지 않고 메운다(타일 수). */
export const MIN_CAVE = 220;

/* 해변 폭. */
export const BEACH_W = 90;          // 물가에서 안쪽으로 이만큼이 모래 해변이다
/* 바다 + 해변 — 나무·풀·꽃 같은 지상 초목을 놓지 않는다 — 사연: docs/code-history.md#h102 */
export const inSeaZone = (x: number, seaX1: any) => x < seaX1 + BEACH_W + 4;
export const BIOME_BAND = 104;     // 바이옴 경계 블렌딩 폭(타일)

/* 조명(computeLight) — 이 세기 이상의 빛나는 칸은 점 광원(그늘을 계산), 거리 한 칸에 줄어드는 양, 그늘로 튀어 들어가는 빛이
   한 칸에 줄어드는 양(작을수록 그림자가 옅다), 빛 색을 얹는 세기. 사연: docs/code-history.md#h142 */
export const LIGHT_POINT = 5, LIGHT_FALL = 1.15, LIGHT_BOUNCE = 3.2, LIGHT_TINT = 0.35;
/* 해(computeLight sun) — 해가 높이 떠 있을 때 그늘진 바깥이 받는 하늘빛 몫 · 잎이 통과시키는 햇빛 몫(나무 그늘이 얼룩진다) */
export const SUN_AMB = 0.4, SUN_SOFT = 0.9, SUN_DEPTH = 10;    // SUN_SOFT — 밟을 수 없는 것(기둥 · 장식)을 지나며 남는 햇빛
export const SUN_PLANT = 0.94, SOIL_R = 5;   // 꽃 · 풀 · 장식이 남기는 햇빛(옅은 그늘) · 흙 속 햇빛을 고르는 반경
export const SUN_BODY = 0.25;   // 사람 몸이 가린 칸에 남는 햇빛(덮인 넓이만큼) — 몸 그림자도 나무 그늘과 같은 직사광 엔진
export const SUN_LEAF = 0.9;   // 잎이 다 붙어 있을 때 남는 햇빛 — 잎이 지면(leafKeep) 1 로 다가간다. 사연: docs/code-history.md#h150
/** 빛을 막는 타일 — 막힌 칸(solid 1) 중 창문(clear)이 아닌 것 */
export const LIGHT_OPAQUE = new Uint8Array(TILE_DEF.length);
TILE_DEF.forEach((d, i) => { LIGHT_OPAQUE[i] = d.solid === 1 && !d.clear ? 1 : 0; });
/** 빛 색 '#rrggbb' → [r, g, b](0~1) */
export const LIGHT_RGB: Record<string, number[]> = {};
for (const d of TILE_DEF) if (d.lc && !LIGHT_RGB[d.lc]) { const v = parseInt(d.lc.slice(1), 16); LIGHT_RGB[d.lc] = [(v >> 16) / 255, ((v >> 8) & 255) / 255, (v & 255) / 255]; }

/** 세계 크기를 정한다 — 새 게임 직전·불러오기 직전에 부른다(치수는 size.js). 다음 new World 가 이 크기로 만들어진다. */
export function setWorldSize(key: string) { applyWorldSize(key); }

/** 그 크기 세계의 바이옴 유적 명세 — 표(RUIN_SPEC)는 소형 기준 그대로 두고 세계마다 자리를 옮긴 복사본을 갖는다(`world.ruinSpec`). */
export function ruinSpecFor(D: WorldDims): RuinDef[] {
  return RUIN_SPEC.map(r => Object.assign({}, r, { x: D.SX(r.x!), y: r.id === 'abyss' ? D.SYB(r.y!) : D.SY(r.y!) }));
}

/* 바이옴이 아닌 구역의 이름표 — 원경 그림이 바뀌는 자리와 짝이다(G.bgId). */
export const ZONE_CARD: Bag = {
  camp: { n: '베이스캠프', sub: '잿빛 숲 한복판',
          card: { line: '살아남은 이들이 처음 불을 피운 자리. 여기서부터 다시 센다.' } },
  village: { n: '여명 마을', sub: '재를 이고 사는 곳',
             card: { line: '재가 내린 뒤에도 굴뚝이 서 있다. 사람이 남긴 마지막 거리.' } }
};

/* 깊이층 — 이름(HUD 고도 옆 s · 처음 들어설 때 카드 n/sub/card). 경계는 world/strata.ts strataLines */
export const DEPTH_LAYER: Bag = {
  surface: { s: '지표' }, sky: { s: '하늘' }, under: { s: '지하' }, hell: { s: '지옥' },
  slate: { s: '심층암층', n: '심층암 지대', sub: '눌려 굳은 검은 켜',
           card: { line: '돌이 소리를 삼킨다. 긴 굴이 어둠 속으로 끝없이 뻗어 있다.' } },
  ember: { s: '작열층', n: '작열 지대', sub: '현무암과 마그마 맥',
           card: { line: '벽 틈으로 붉은 맥이 뛴다. 발밑 어딘가에서 지옥이 숨을 쉰다.' } }
};
/* 깊이층 벽지(심층암 · 현무암) — 지층 벽지와 같이 '자연 굴'로 센다 */
export const DEPTH_WALL = [17, 18];

/* 재질 번호 → 지층 구성 — 사연: docs/code-history.md#h103 */
export const MAT_LAYER = [
  { top: T.SNOW, soil: T.SNOW, sub: T.ICE, deep: T.STONE, wall: 5, subWall: 2 },
  { top: T.GRASS, soil: T.DIRT, sub: T.DIRT, deep: T.STONE, wall: 1, subWall: 2 },
  { top: T.SAND, soil: T.SAND, sub: T.SANDSTONE, deep: T.STONE, wall: 8, subWall: 2 },
  { top: T.CORRUPTGRASS, soil: T.CORRUPTGRASS, sub: T.EBONSTONE, deep: T.EBONSTONE, wall: 3, subWall: 3 },
  { top: T.JUNGLEGRASS, soil: T.MUD, sub: T.MUD, deep: T.STONE, wall: 11, subWall: 11 },
  { top: T.GLOWMOSS, soil: T.DIRT, sub: T.SPORESTONE, deep: T.STONE, wall: 12, subWall: 12 },
  // 6 빙하 — 흙 한 겹 없이 얼음이 그대로 두껍게 쌓여 있다
  { top: T.SNOW, soil: T.ICE, sub: T.ICE, deep: T.ICE, wall: 5, subWall: 5 },
  // 7 바다 — 지면은 해저 모래다
  { top: T.SAND, soil: T.SAND, sub: T.SANDSTONE, deep: T.STONE, wall: 8, subWall: 2 }
];
/** 자연 벽지 — 지층 벽지 + 깊이층 벽지. 유적 · 건물 벽지와 가른다 */
export function naturalWalls() {
  const s = new Set<number>(DEPTH_WALL);
  for (const L of MAT_LAYER) { s.add(L.wall); s.add(L.subWall); }
  return s;
}
export const MAT_OF: Record<string, number> = { ice: 0, forest: 1, forest2: 1, desert: 2, corrupt: 3, jungle: 4, glowfen: 5,
                 glacier: 6, sea: 7 };

/* ================= 여명 마을 배치표 ================= */
export const DAWN_BUILDINGS = [
  // off: 시작 칸, w/h: 폭·높이, npc/fac: 그 안에 사는 사람과 시설.
  { off:  2, w: 18, h: 10, npc: 'haran',   fac: 'inn'       },   // 여관
  { off: 24, w: 17, h: 13, npc: 'tamer',   fac: 'workbench' },   // 조련사
  { off: 70, w: 17, h: 13, npc: 'seira',   fac: 'reforge'   },   // 재련
  { off: 93, w: 18, h: 10, npc: 'trainer', fac: 'vault'     }    // 훈련소 · 보관고
];
/* 건물 안 배치 — 건물 시작점 기준. */
/* 시설(fac)은 9~10칸, 강화 모루는 11~12칸, 탁자는 14~15칸. */
export const DAWN_INSIDE = { shelf: 2, npc: 6, fac: 9, anvil: 11, table: 14 };

/* 광장 — 건물1과 건물2 사이. */
// 작업대는 조련사네 집 안(DAWN_BUILDINGS[1].fac)으로 옮겨서 여기 목록엔 없다.
export const DAWN_PLAZA = [
  { id: 'kade',      off: -10, w: 1 },
  { id: 'board',     off:  -7, w: 2 },
  { id: 'fountain',  off:  -2, w: 5 },
  { id: 'townhall',  off:   4, w: 2 },
  { id: 'waystone',  off:   8, w: 2 },
  { id: 'forge',     off:  12, w: 2 }
];
/* 광장 물건의 실제 그림 크기(px). */
export const DAWN_OBJ: Bag = {
  // 베이스캠프·여명 마을·플레이어가 직접 놓는 것 전부 같은 크기(OBJ_SIZE)를 쓴다
  workbench: { type: 'workbench', w: OBJ_SIZE.workbench.w, h: OBJ_SIZE.workbench.h, lv: 1 },
  kade:      { type: 'npc', npc: 'kade', w: 22, h: 44 },
  board:     { type: 'board', w: 34, h: 44 },
  fountain:  { type: 'fountain', w: 110, h: 66 },
  townhall:  { type: 'townhall', w: 36, h: 46 },
  waystone:  { type: 'waystone', w: 30, h: 48 },
  forge:     { type: 'forge', w: OBJ_SIZE.forge.w, h: OBJ_SIZE.forge.h, lv: 1 },
  // 건물 안에 놓이는 것들 (DAWN_BUILDINGS의 npc/fac가 가리킨다) 침대는 2칸 슬롯(44px)을 꽉 채운다 — 그리는 쪽(game.js drawFacility)도 발판을
  // o.h 바닥에 붙인다
  inn:       { type: 'inn', w: 44, h: 34 },
  reforge:   { type: 'reforge', w: 44, h: 40 },
  anvil:     { type: 'anvil', w: 44, h: 44 },      // 강화 모루 — 2×2칸. 4단계에서 재련대 옆에 선다

  vault:     { type: 'vault', w: 40, h: 36 },
  // 가구는 다른 물건 옆에서 왜소해 보이지 않게 — 책장은 작업대·용광로와 같은 2칸(44px) (DAWN_INSIDE.shelf 슬롯도 2칸), 탁자는 슬롯을 2칸으로 늘려서 키운다.
  shelf:     { type: 'furniture', kind: 'shelf', w: 36, h: 42, tw: 2, th: 2 },
  table:     { type: 'furniture', kind: 'table', w: 28, h: 20 },
  npcBase:   { type: 'npc', w: 22, h: 44 }   // 건물 주민 — npc 이름만 갈아 끼워 쓴다
};
/* 3단계 성벽 — 마을 양 끝 바깥. */
export const DAWN_WALL = { leftOff: -16, rightOff: 15, gateH: 3, towerH: 14,
  /* 성벽에서 이만큼 더 바깥까지 지면을 평평하게 깎는다. */
  flatPad: 10 };

/** 닫힌 문 = 옆에서 본 문짝 — 경첩 쪽 가장자리의 얇은 판만 막는다(열린 문은 칸을 채운 앞면이고 안 막는다).
    game.js drawDoor 가 같은 폭으로 그린다. */
export function doorEdge(d: any) {
  const w = Math.max(6, Math.round(d.w * 0.32));
  return { x: d.dir === -1 ? d.x : d.x + d.w - w, y: d.y, w, h: d.h };
}

/** Ashfall 세계 — 타일맵(engine/tilemap) 위에 생성기 · 마을 · 유적 · 바다 · 유체 · 조명 규칙을 얹는다(엔진화 계획 §8-4 상속). */
export class World extends TileMap {
  /* 필드 — 생성자·조각이 채운다. 타입은 차례로 좁힌다 */
  declare _entranceLandX: number | null; declare _entranceLandY: number | undefined; declare _entranceRooms: any[]; declare _entranceSpots: any[];
  declare _natural: Set<any>; declare _ruinCtx: Record<string, any> | null; declare isle: Record<string, any>; declare jungleLake: Record<string, any>;
  declare richCount: number; declare richSites: number; declare stamp: number; declare ruinVaults: any[]; declare seaBed: Int16Array; declare yunseul: Record<string, any>;
  declare _airPocket: (...a: any[]) => any; declare _bedAt: (...a: any[]) => any; declare _buildPassage: (...a: any[]) => any;
  declare _canDecor: (...a: any[]) => any; declare _canopy: (...a: any[]) => any; declare _carveBasin: (...a: any[]) => any;
  declare _carveEntranceShaft: (...a: any[]) => any; declare _cgW: (...a: any[]) => any; declare _decorOne: (...a: any[]) => any;
  declare _deepFloor: (...a: any[]) => any; declare _digStair: (...a: any[]) => any; declare _ensureConnected: (...a: any[]) => any;
  declare _fillBasin: (...a: any[]) => any; declare _finishPyramid: (...a: any[]) => any; declare _floorBelow: (...a: any[]) => any;
  declare _floorNear: (...a: any[]) => any; declare _groundTrunk: (...a: any[]) => any; declare _levelLiquid: (...a: any[]) => any;
  declare _linkRooms: (...a: any[]) => any; declare _noWater: (...a: any[]) => any; declare _returnSet: (...a: any[]) => any;
  declare _skyClear: (...a: any[]) => any; declare _skyStrip: (...a: any[]) => any; declare _standFns: (...a: any[]) => any;
  declare _standNext: (...a: any[]) => any; declare _standSeed: (...a: any[]) => any; declare _standSet: (...a: any[]) => any;
  declare _walkBack: (...a: any[]) => any; declare _walkPass: (...a: any[]) => any; declare _walkable: (...a: any[]) => any;
  declare bspSplit: (...a: any[]) => any; declare buildCipherVault: (...a: any[]) => any; declare buildFaults: (...a: any[]) => any;
  declare buildMysticRoom: (...a: any[]) => any; declare buildRuinSite: (...a: any[]) => any; declare buildSigRoom: (...a: any[]) => any;
  declare buildSkyExtras: (...a: any[]) => any; declare buildSkyTemple: (...a: any[]) => any; declare carveDungeon: (...a: any[]) => any; declare _wearRoom: (...a: any[]) => any; declare _growHall: (...a: any[]) => any; declare _carveGrand: (...a: any[]) => any; declare _tallLink: boolean; declare grandLights: (...a: any[]) => any; declare _inBossRoom: (x: number, y: number) => boolean;
  declare carveIsland: (...a: any[]) => any; declare carveRuinEntrance: (...a: any[]) => any; declare caveTypeAt: (...a: any[]) => any;
  declare checkDawnLayout: (...a: any[]) => any; declare clearBox: (...a: any[]) => any; declare clearRigSite: (...a: any[]) => any;
  declare dawnPlace: (...a: any[]) => any; declare dawnPlazaSpan: (...a: any[]) => any; declare faultCells: (...a: any[]) => any;
  declare fitRig: (...a: any[]) => any; declare inRig: (...a: any[]) => any; declare locked: (...a: any[]) => any;
  declare placeMerchants: (...a: any[]) => any; declare poleColumn: (...a: any[]) => any; declare putDecor: (...a: any[]) => any;
  declare putPathTrap: (...a: any[]) => any; declare putRuinDecor: (...a: any[]) => any; declare putTileTrap: (...a: any[]) => any;
  declare skyAlt: (...a: any[]) => any; declare skyFeature: (...a: any[]) => any; declare skyGrotto: (...a: any[]) => any;
  declare trapSpot: (...a: any[]) => any;
  declare storyMemories: (...a: any[]) => void; declare settleMemories: () => void; declare _memEnt: number[][];
  declare clearSkyBlobs: (zones: number[][]) => void; declare _citadelVault: (...a: any[]) => Bag | null;
  declare sculptGlacier: () => void; declare softenFrost: () => void; declare _coldFree: (x: number, pad?: number) => boolean; declare _resurf: (x: number) => void;
  declare applyStrata: () => void; declare buildTunnels: () => void; declare depthLayer: (tx: number, ty: number) => string;
  declare strataLines: (tx: number) => number[]; declare tunnels: Bag[]; declare _strataPh: number[]; declare _tunBoxes: number[][] | null; declare _tunNat: Set<number> | null;
  declare _tunOk: (x: number, y: number) => boolean; declare _tunBlob: (...a: any[]) => boolean; declare _tunWorm: (...a: any[]) => number; declare _tunFloor: (x: number, y: number, lim: number) => number;
  declare dims: WorldDims; declare ruinSpec: RuinDef[]; declare netLog: Set<number> | null; declare netMute: boolean;
  declare _ensureWalkable: (...a: any[]) => any; declare _walkJobs: any[]; declare atelier: Record<string, any>; declare beach: Record<string, any>; declare breakLongRuns: (...a: any[]) => any;
  declare buildAltars: (...a: any[]) => any; declare buildAtelier: (...a: any[]) => any; declare buildCaveZones: (...a: any[]) => any; declare buildCaverns: (...a: any[]) => any;
  declare buildCitadel: (...a: any[]) => any; declare buildDawnCity: (...a: any[]) => any; declare clearSkyOverSettlements: () => void; declare buildDeepShaft: (...a: any[]) => any; declare buildDungeon: (...a: any[]) => any;
  declare buildJungleFalls: (...a: any[]) => any; declare buildRuinCaches: (...a: any[]) => any; declare buildRuins: (...a: any[]) => any; declare buildRunaway: (...a: any[]) => any;
  declare buildSea: (...a: any[]) => any; declare buildSkyIslands: (...a: any[]) => any; declare buildVillage: (...a: any[]) => any; declare buildWorks: (...a: any[]) => any;
  declare cactusPlant: (...a: any[]) => any; declare caveGrid: Uint8Array | null; declare caverns: any[]; declare citadel: Record<string, any>; declare crops: Set<any>; declare built: Set<number>; declare wet: Record<string, any>; declare isWet: (...a: any[]) => any; declare waterFarm: (...a: any[]) => any; declare nearWater: (...a: any[]) => any;
  declare crumbled: Map<any, any>; declare dawnCity: Record<string, any>; declare dawnY: number; declare decoratePonds: (...a: any[]) => any; declare decorateWater: (...a: any[]) => any;
  declare deepShaft: Record<string, any>; declare doors: any[]; declare dungeon: Record<string, any>; declare ensureEntranceTraps: (...a: any[]) => any; 
  declare falls: any[]; declare faults: any[]; declare fillMossCorners: (...a: any[]) => any; declare fitObjects: (...a: any[]) => any; declare floodCaves: (...a: any[]) => any;
  declare floodHell: (...a: any[]) => any; declare decorateHell: (...a: any[]) => any; declare liq: Uint8Array; declare murk: Uint8Array; declare fallF: Uint8Array; declare fstep: number; declare murkSet: Set<number>; declare _liqLoad: number[] | null;
  declare _fD: Int16Array; declare _fK: Uint8Array; declare _fT: Uint8Array; declare _fTouch: number[]; declare _fSet: number[][]; declare _fFall: number[]; declare fxQ: number[][] | null;
  declare fq: import('../engine/tilemap/cellqueue.js').CellQueue<null> | null;
  declare amtAt: (k: number) => number; declare liqFrac: (k: number) => number; declare fluidInit: () => void; declare fluidWake: (x: number, y: number) => void;
  declare fluidTick: (dt: number) => void; declare fluidSettle: () => void; declare fallsAll: () => void; declare _fallsCol: (x: number, y: number) => void;
  declare currentAt: (x: number, y: number) => number; declare liqSave: () => number[]; declare pourFluid: (x: number, y: number, kind: number, amount: number) => number;
  declare _fluidStep: (j: number) => void; declare _flowCell: (k: number, j: number) => void; declare _fluidCommit: () => void; declare _murkStep: () => void;
  declare _fcur: (k: number) => number; declare _fmove: (a: number, b: number, m: number, kind: number) => void; declare _ftouch: (k: number) => void;
  declare _fwake: (k: number) => void; declare _spring: (k: number) => void; declare _frnd: (k: number) => number; declare _fallsSet: (n: number, on: boolean) => void; declare hangQ: number[] | null; declare giantTree: Record<string, any>; declare glowStalk: (...a: any[]) => any;
  declare inAtelier: (...a: any[]) => any; declare inCitadel: (...a: any[]) => any; declare inDeepShaft: (...a: any[]) => any; declare inRuin: (...a: any[]) => any; declare ruinInside: (...a: any[]) => any; declare storyTheme: (...a: any[]) => any; declare inRunaway: (...a: any[]) => any;
  declare inWorks: (...a: any[]) => any; declare jungleTree: (...a: any[]) => any; declare lavaPools: any[]; declare light: LightField | null;
  declare machines: Map<any, any>; declare mpart: Map<number, number>; declare matId: Uint8Array; declare netDirty: boolean; declare nets: any[];
  declare objects: any[]; declare openCodeDoorway: (...a: any[]) => any; declare oreHits: Record<string, any>; declare pineTree: (...a: any[]) => any; declare placeRichOres: (...a: any[]) => any;
  declare placeRigs: (...a: any[]) => any; declare pools: any[]; declare pruneSmallCaves: (...a: any[]) => any; declare restoreSealRoom: (...a: any[]) => any; declare rng: RNG;
  declare ruinAt: (...a: any[]) => any; declare ruinEvents: any[]; declare ruinSites: any[]; declare ruins: any[]; declare runaway: Record<string, any>;
  declare scatterChests: (...a: any[]) => any; declare sea: Record<string, any>; declare seaLevel: number; declare sealCipherVaults: (...a: any[]) => any; declare sealLiquids: (...a: any[]) => any;
  declare sealRoom: Record<string, any>; declare seed: string; declare shoreY: number; declare skyGate: Record<string, any>; declare skyIslands: any[];
  declare spawnX: number; declare spawnY: number; declare surface: Int16Array; declare sweepFloatingDecor: (...a: any[]) => any; declare pruneBrokenTrees: () => number; declare restoreCanopies: () => number; declare _canopyLog: any[] | null; declare sweepPockets: (...a: any[]) => any;
  declare tree: (...a: any[]) => any; declare villageY: number; declare works: Record<string, any>;

  constructor(seed: string) {
    const D = dimsOf(), { WW, WH } = D;         // setWorldSize 가 고른 크기 — 이 세계가 제 것으로 가져간다
    super(WW, WH, TS, TILE_DEF, T.BEDROCK);   // 타일 · 벽지 · 탐험 배열, 경계 밖 = 기반암
    this.dims = D;
    this.ruinSpec = ruinSpecFor(D);             // 이 세계의 유적 자리(mystic 도 여기에 적는다)
    this.seed = seed;
    this.rng = new RNG(seed);
    this.surface = new Int16Array(WW);
    this.objects = [];          // 작업대/용광로/상자/NPC/제단
    this.doors = [];            // objects의 부분집합(같은 참조) — 충돌 판정을 빠르게 하려고 따로 캐싱
    /* 공장 기계. */
    this.machines = new Map();
    this.mpart = new Map();          // 여러 칸 기계의 몸 칸 → 닻 칸(Factory.claim) — 저장하지 않고 machines 에서 다시 건다
    this.oreHits = {};          // 칸 → 드릴이 더 캘 수 있는 횟수(처음 캘 때 매긴다)
    this.netDirty = true;       // 전력망을 다시 계산해야 하는가 (기계 설치/철거 시 켜진다)
    this.nets = [];
    /* 심어 둔 작물의 타일 인덱스. */
    this.crops = new Set();
    this.wet = {};              // 밭 칸 → 이 날(G.dayCount)까지 젖어 있다
    this.built = new Set();     // 플레이어가 놓은 칸 — 보스 싸움터를 넓힐 때 건드리지 않는다(game/boss-arena)
    /* 부서진 바닥이 되돌아올 시각. */
    this.crumbled = new Map();
    this.spawnX = 180; this.spawnY = 0;
    this.light = null;
  }

  set(x: number, y: number, t: any) { const { WW } = this.dims;
    if (!this.inB(x, y)) return;
    const k = y * WW + x, was = this.tiles[k];
    this.tiles[k] = t; this.stamp = (this.stamp | 0) + 1;   // 바뀜 표 — 타일 청크 캐시가 이것이 그대로면 대조를 쉰다(game/render-chunks)
    if (this.light && was !== t) this.lightTouch(k, was);
    /* 유체가 켜진 뒤(생성·불러오기 끝)에만 — 바뀐 칸과 그 네 이웃을 흐름 검사 줄에 세운다. */
    if (this.fq) this.fluidWake(x, y);
    if (this.hangQ && was !== t) this.hangQ.push(k);   // 매달린·세운 장식이 기댈 데를 잃었나(game/caves.ts updateHang)
    if (this.netLog && !this.netMute) this.netLog.add(y * WW + x);   // 멀티플레이 — 바뀐 칸을 모아 보낸다(game/net.ts)
  }
  setWall(x: any, y: any, w: any) {
    super.setWall(x, y, w); this.stamp = (this.stamp | 0) + 1;
    if (this.netLog && !this.netMute && this.inB(x, y)) this.netLog.add(y * this.dims.WW + x);
  }
  /** tiles 를 직접 쓴 칸(작물 자람 · 무너지는 바닥)도 멀티플레이 기록에 남긴다. */
  netMark(k: any) { this.stamp = (this.stamp | 0) + 1; if (this.netLog && !this.netMute) this.netLog.add(k); if (this.light) this.lightTouch(k, -1); }
  hurtTile(x: number, y: number) { return TILE_DEF[this.get(x, y)].hurt || 0; }
  /** 사각형이 물에 얼마나 잠겼는지 0~1. */
  liquidIn(px: any, py: any, w: any, h: any) { const { WW } = this.dims;
    const x0 = Math.floor(px / TS), x1 = Math.floor((px + w - 0.01) / TS);
    const y0 = Math.floor(py / TS), y1 = Math.floor((py + h - 0.01) / TS);
    let n = 0, tot = 0, flow = 0, cur = 0;
    for (let x = x0; x <= x1; x++) for (let y = y0; y <= y1; y++) {
      tot++;
      const t = this.get(x, y), d = TILE_DEF[t];
      if (!d.liquid) continue;
      if (d.flow) flow = 1;
      /* 흐르는 물은 수위만큼만 잠긴 것으로 친다 — 발목 깊이 물에서 헤엄치게 되면 안 된다. */
      if (FLUID_FLOW[t] && this.liq) {
        const k = y * WW + x, lv = this.liq[k] || 255;
        const topped = FLUID_KIND[this.tiles[k - WW]] === FLUID_KIND[t];
        n += topped ? 1 : lv / 255;
        if (lv < 255 && !cur) cur = this.currentAt(x, y);       // 겹친 칸 중 처음 흐르는 칸 하나로 — 더하면 칸 수만큼 세진다
      } else n++;
    }
    return { f: tot ? n / tot : 0, flow, cur };
  }
  /** (tx, ty) 칸이 물이면 그 물기둥의 **맨 윗칸**을 찾는다(최대 lim 칸 위까지). */
  surfaceRow(tx: number, ty: number, lim: any) {
    if (!TILE_DEF[this.get(tx, ty)].liquid) return -1;
    for (let y = ty; y > ty - lim && y > 1; y--)
      if (!TILE_DEF[this.get(tx, y - 1)].liquid) return this.get(tx, y - 1) === T.AIR ? y : -1;
    return -1;
  }
  /** 사각형이 겹치거나 맞닿은 타일 중 가장 큰 hurt값을 돌려준다. */
  hurtInRect(px: any, py: any, w: any, h: any) {
    const pad = 1;
    const x0 = Math.floor((px - pad) / TS), x1 = Math.floor((px + w - 0.01 + pad) / TS);
    const y0 = Math.floor((py - pad) / TS), y1 = Math.floor((py + h - 0.01 + pad) / TS);
    let m = 0;
    for (let x = x0; x <= x1; x++) for (let y = y0; y <= y1; y++) {
      const hv = TILE_DEF[this.get(x, y)].hurt || 0;
      if (hv > m) m = hv;
    }
    return m;
  }

  biomeAt(tx: number) { const { BIOMES } = this.dims;
    for (const b of BIOMES) if (tx >= b.x0 && tx < b.x1) return b;
    return BIOMES[1];
  }
  biomeIndexAt(tx: number) { const { BIOMES } = this.dims;
    for (let i = 0; i < BIOMES.length; i++) if (tx >= BIOMES[i].x0 && tx < BIOMES[i].x1) return i;
    return tx < 0 ? 0 : BIOMES.length - 1;
  }
  /** 경계 혼합: [주 바이옴, 이웃 바이옴, 이웃 비중 0~0.5] */
  biomeMix(tx: number) { const { BIOMES } = this.dims;
    const i = this.biomeIndexAt(tx), b = BIOMES[i];
    if (i > 0 && tx - b.x0 < BIOME_BAND) return [i, i - 1, 0.5 * (1 - (tx - b.x0) / BIOME_BAND)];
    if (i < BIOMES.length - 1 && b.x1 - tx <= BIOME_BAND) return [i, i + 1, 0.5 * (1 - (b.x1 - tx) / BIOME_BAND)];
    return [i, i, 0];
  }
  /** 바이옴별 지표 높이 (경계에서 부드럽게 이어지도록 x 전 구간에서 정의) */
  _hFor(bid: string, x: number, n1: any) { const { SURF_BASE } = this.dims;
    let h = SURF_BASE + (n1(x, 0.011) - 0.5) * 32 + (n1(x + 900, 0.042) - 0.5) * 10;
    if (bid === 'desert') h += 8 + (n1(x + 400, 0.025) - 0.5) * 12;
    else if (bid === 'ice') h -= 8 + (n1(x + 1500, 0.06) - 0.5) * 6;
    /* 빙하는 서리 지대보다 더 높이 솟고 능선이 거칠다. */
    else if (bid === 'glacier') h -= 16 + (n1(x + 2600, 0.09) - 0.5) * 22;
    else if (bid === 'sea') h -= 2 + (n1(x + 5300, 0.05) - 0.5) * 6;
    else if (bid === 'corrupt') h += 2 + (n1(x + 2200, 0.075) - 0.5) * 24;
    // 정글은 골짜기가 깊게 파이고, 버섯 골짜기는 이름대로 통째로 내려앉아 있다
    else if (bid === 'jungle') h += 6 + (n1(x + 3300, 0.05) - 0.5) * 18;
    else if (bid === 'glowfen') h += 16 + (n1(x + 4100, 0.03) - 0.5) * 10;
    return h;
  }
  /** 이 x에서 어떤 바이옴의 '재질'을 쓸지 — 경계에서는 노이즈로 맞물리게 */
  _matAt(x: number, n1: any) { const { BIOMES } = this.dims;
    const [ia, ib, t] = this.biomeMix(x);
    if (t <= 0.001 || ia === ib) return BIOMES[ia].id;
    return n1(x + 7777, 0.11) < t ? BIOMES[ib].id : BIOMES[ia].id;
  }
  /** 전투/스폰용 구역 태그 */
  zoneAt(tx: number, ty: number) { const { WW, HELL_Y, DEEP_Y, SKY_Y, CAMP_X0, CAMP_X1 } = this.dims;
    // 특별 유적 둘은 각각 하늘·지옥 판정보다 먼저 본다 — 안에 들어와 있으면 그 구역이 우선이다
    if (this.inCitadel && this.inCitadel(tx, ty)) return 'citadel';
    if (this.inDeepShaft && this.inDeepShaft(tx, ty)) return 'deepshaft';
    if (ty < SKY_Y) return 'sky';
    // 베이스캠프·여명 마을 — 안전 지대.
    if (tx >= CAMP_X0 - 16 && tx <= CAMP_X1 + 16 && ty < this.surface[clamp(tx, 0, WW - 1)] + 20) return 'camp';
    if (this.dawnCity) {
      const d = this.dawnCity;
      if (tx >= d.x0 - 16 && tx <= d.x1 + 16 && ty < d.gy + 20) return 'village';
    }
    if (this.inAtelier && this.inAtelier(tx, ty)) return 'atelier';
    if (this.inRunaway && this.inRunaway(tx, ty)) return 'runaway';
    if (this.inWorks && this.inWorks(tx, ty)) return 'works';
    if (this.inRuin && this.inRuin(tx, ty)) return 'ruin';
    if (ty >= HELL_Y) return 'hell';
    const b = this.biomeAt(tx).id;
    if (ty > this.surface[clamp(tx, 0, WW - 1)] + 6) {
      if (ty > DEEP_Y) return 'deep';
      if (b === 'corrupt') return 'corrupt';
      // 빙하 지대도 얼음 구역으로 친다 — 몹 표는 zoneTable이 바이옴으로 한 번 더 가른다
      if (b === 'ice' || b === 'glacier') return 'ice';
      if (b === 'jungle') return 'jungle';       // 정글은 지하도 정글이다 (뿌리와 진흙층)
      if (b === 'glowfen') return 'glowfen';
      return 'cave';
    }
    if (b === 'corrupt') return 'corrupt';
    // 해변 — 물가 안쪽 모래밭.
    if (this.beach && tx >= this.beach.x0 && tx <= this.beach.x1) return 'beach';
    if (b === 'ice' || b === 'glacier') return 'ice';
    if (b === 'jungle') return 'jungle';
    if (b === 'glowfen') return 'glowfen';
    if (b === 'sea') return 'sea';              // 바다 — 지상 몹 표를 안 태운다
    return 'surface';
  }
  /** 상자 보상은 지형 이름이 아니라 실제 위치 프로필로 고른다. */
  chestLootProfile(tx: number, ty: number) {
    const zone = this.zoneAt(tx, ty);
    if (zone === 'works' || zone === 'runaway' || zone === 'atelier'
      || zone === 'citadel' || zone === 'deepshaft') return 'session2';
    return zone === 'ruin' ? 'ruin' : 'world';
  }

  /* ================= 생성 ================= */
  generate() { const { WSX, WSY, SX, SY, WW, WORLD_BOT, SURF_BASE, HELL_Y, DEEP_Y, CAMP_X0, CAMP_GX1, SEA_X1, BIOMES } = this.dims;
    const rng = this.rng;
    this._canopyLog = [];            // 수관 기록 — 끝에서 깎인 잎을 되채운다(restoreCanopies)
    const n1 = makeNoise1D(new RNG(rng.next() * 1e9), 4);
    const n2 = makeNoise2D(new RNG(rng.next() * 1e9));
    const n3 = makeNoise2D(new RNG(rng.next() * 1e9));

    // --- 1. 지표선 (바이옴 경계에서 높이를 선형 혼합해 단차 제거) ---
    const rawH = new Float32Array(WW);
    for (let x = 0; x < WW; x++) {
      const [ia, ib, t] = this.biomeMix(x);
      const ha = this._hFor(BIOMES[ia].id, x, n1);
      const h = (t > 0.001 && ia !== ib) ? lerp(ha, this._hFor(BIOMES[ib].id, x, n1), t) : ha;
      // 하한을 하늘 구역(SKY_Y) 위로 넉넉히 띄워, 이중 점프로도 지상에서 하늘 구역에 닿지 않게 한다 기준 지표에서 -12 ~ +38 (소형 58~108).
      rawH[x] = clamp(h, SURF_BASE - 12, SURF_BASE + 38);
    }
    // 3칸 이동평균으로 남은 계단 제거
    for (let x = 0; x < WW; x++) {
      const a = rawH[Math.max(0, x - 1)], b2 = rawH[x], c2 = rawH[Math.min(WW - 1, x + 1)];
      this.surface[x] = Math.round((a + b2 * 2 + c2) / 4);
    }
    /* 해수면이 고도 0m 이고 땅은 그 밑으로 안 내려간다 — 수면은 물가 지면에서 정해지므로(buildSea), 물가 쪽 땅을
       뭍에서 가장 낮은 골(가장 큰 y)까지 천천히 내려 바다를 세계에서 가장 낮은 곳에 앉힌다. 사연: docs/code-history.md#h164 */
    let low = 0;
    for (let x = SEA_X1 + 1; x < WW; x++) if (this.surface[x] > low) low = this.surface[x];
    const COAST = SX(220);
    for (let x = 0; x < Math.min(WW, SEA_X1 + COAST); x++) {
      const t = clamp((x - SEA_X1) / COAST, 0, 1), k = t * t * (3 - 2 * t);
      this.surface[x] = Math.round(lerp(low, this.surface[x], k));
    }
    // 마을 부지 평탄화
    const vx0 = CAMP_X0, vx1 = CAMP_GX1;   // 베이스캠프 — 잿빛 숲 (생성 발자국)
    let vh = this.surface[(vx0 + vx1) >> 1];
    for (let x = vx0 - 12; x < vx1 + 12; x++) {
      const t = clamp(inv(vx0 - 12, vx0, x), 0, 1) * clamp(inv(vx1 + 12, vx1, x), 0, 1);
      this.surface[x] = Math.round(lerp(this.surface[x], vh, Math.min(1, t * 1.6)));
    }
    this.villageY = vh;

    // 여명 마을 부지 (동쪽 숲) — 잿빛에 묻힌 폐허로 미리 세워 두고, 종장 이후 되살린다
    const dx0 = SX(2905 + SHIFT) - 55, dx1 = dx0 + 110;    // 여명 마을 — 동쪽 숲 (폭 110은 그대로, 가운데만 옮긴다)
    const dh = this.surface[(dx0 + dx1) >> 1];
    /* ★ 평탄화는 **3단계 성벽 자리 바깥까지** 완전히 평평해야 한다. */
    const padL = -DAWN_WALL.leftOff + DAWN_WALL.flatPad;
    const padR = DAWN_WALL.rightOff + DAWN_WALL.flatPad;
    const ramp = 8;                        // 마을 바깥으로 자연 지형에 녹아드는 구간
    for (let x = dx0 - padL; x < dx1 + padR; x++) {
      const t = clamp(inv(dx0 - padL, dx0 - padL + ramp, x), 0, 1) *
                clamp(inv(dx1 + padR, dx1 + padR - ramp, x), 0, 1);
      this.surface[x] = Math.round(lerp(this.surface[x], dh, Math.min(1, t * 1.6)));
    }
    this.dawnY = dh;

    // --- 2. 기본 지층 (재질도 경계에서 맞물리게) ---
    this.matId = new Uint8Array(WW);
    for (let x = 0; x < WW; x++) this.matId[x] = MAT_OF[this._matAt(x, n1)];
    for (let x = 0; x < WW; x++) {
      const s = this.surface[x], m = this.matId[x];
      // 표층 두께도 조금씩 흔들어 층이 일직선으로 보이지 않게
      const soilD = 4 + Math.round(n1(x + 3100, 0.09) * 3);
      const subD = 15 + Math.round(n1(x + 5200, 0.07) * 8);
      const L = MAT_LAYER[m];
      // 지옥 경계는 한 줄이 아니다 — 들쭉날쭉한 선 위 6칸에 재가 점점 섞인다
      const hb = HELL_Y - 4 + Math.round(n1(x + 7700, 0.05) * 5);
      // 땅 밑 기반암도 한 줄로 자르지 않는다 — 두께 3~7칸으로 출렁인다
      const bt = 3 + Math.round(Math.abs(n1(x + 9100, 0.06)) * 4);
      for (let y = s; y < WORLD_BOT; y++) {
        let t;
        const depth = y - s;
        if (y >= WORLD_BOT - bt) t = T.BEDROCK;
        else if (y >= hb || (y >= hb - 6 && ((x * 73 + y * 151) % 7) < y - hb + 7 - 3)) t = T.ASH;
        else if (depth === 0) t = L.top;
        else if (depth < soilD) t = L.soil;
        else if (depth < subD) t = L.sub;
        else t = L.deep;
        if (y > DEEP_Y && t === T.STONE && n3(x, y, 0.06, 2) > 0.72) t = T.OBSIDIAN;
        this.tiles[this.i(x, y)] = t;
        this.walls[this.i(x, y)] = t === T.ASH && y >= hb - 6 ? 7 : depth < subD - 3 ? L.wall : L.subWall;
      }
    }

    // --- 3. 동굴 ---
    /* ★ 동굴은 눈에 띄게 크다 — 노이즈 결이 굵고(0.058, 깊은 곳 0.045) 띠가 넓어서(0.60~0.83) 굴 하나하나가 길고 넓다. */
    for (let x = 1; x < WW - 1; x++) {
      const s = this.surface[x];
      for (let y = s + 4; y < WORLD_BOT - 5; y++) {
        if (this.tiles[this.i(x, y)] === T.BEDROCK) break;   // 출렁이는 기반암(3~7칸)은 파지 않는다
        const scale = y > DEEP_Y - 36 ? 0.045 : 0.058;
        let v = n2(x, y, scale, 3);
        // 깊을수록 큰 공동.
        const bias = y > DEEP_Y ? 0.06 : y > SY(180) ? 0.03 : 0;
        const wide = y > s + 20 ? 0.03 : 0;
        if (v > 0.63 - bias - wide && v < 0.80 + bias + wide) this.tiles[this.i(x, y)] = T.AIR;
        // 좁은 통로
        if (n3(x, y, 0.13, 2) > 0.80 && y > s + 10) this.tiles[this.i(x, y)] = T.AIR;
      }
    }

    // --- 3-B. 자잘한 구멍 메우기 ---
    this.pruneSmallCaves(MIN_CAVE);

    // --- 4. 부패 지대 균열 ---
    for (const b of BIOMES) {
      if (b.id !== 'corrupt') continue;
      for (let k = 0; k < Math.round(24 * WSX); k++) {
        let cx = rng.int(b.x0 + 8, b.x1 - 8), cy = this.surface[cx];
        let w = rng.range(3, 6);
        while (cy < DEEP_Y && w > 0.8) {
          for (let x = Math.floor(cx - w); x <= cx + w; x++)
            for (let y = cy; y < cy + 3; y++) this.set(x, y, T.AIR);
          for (let x = Math.floor(cx - w) - 2; x <= cx + w + 2; x++)
            for (let y = cy; y < cy + 3; y++) if (this.get(x, y) !== T.AIR) this.set(x, y, T.EBONSTONE);
          cx += rng.range(-1.2, 1.2); cy += 2; w -= rng.range(0.02, 0.14);
        }
      }
    }

    // --- 5. 광맥 ---
    const oreSpec = [
      [T.COPPER, SY(74), SY(200), 700, 5],
      [T.IRON, SY(100), SY(300), 730, 5],
      [T.GOLD, SY(150), SY(360), 480, 4],
      [T.MYTHRIL, SY(240), SY(400), 360, 4],
      [T.CRYSTAL, SY(190), SY(390), 280, 3],
      [T.SOULSTONE, SY(300), SY(420), 200, 3],
      [T.HELLSTONE, HELL_Y, WORLD_BOT - 6, 600, 5],
      /* --- 동력 자원 --- */
      [T.COAL, SY(80), HELL_Y - 10, 1500, 6],
      [T.LEAD, SY(110), SY(330), 560, 5],
      [T.OILSHALE, SY(150), SY(300), 620, 6, SX(2000 + SHIFT), SX(2680 + SHIFT)]   // 사막 구간 — 세계가 왼쪽으로 밀린 만큼 같이 민다
    ];
    /* 표의 개수는 폭 2800 기준이라, 세계가 넓어진 만큼 그대로 곱해 밀도를 유지한다. */
    const oreScale = WW / 2800;
    for (const [tile, y0, y1, count, size, ox0, ox1] of oreSpec) {
      const n = Math.round(count * (ox0 === undefined ? oreScale : WSX) * WSY);
      for (let k = 0; k < n; k++) {
        const cx = rng.int(ox0 === undefined ? 2 : ox0, ox1 === undefined ? WW - 3 : ox1 - 1);
        const cy = rng.int(y0, y1);
        const r = rng.range(1.4, size * 0.5 + 1.4);
        for (let x = Math.floor(cx - r); x <= cx + r; x++)
          for (let y = Math.floor(cy - r); y <= cy + r; y++) {
            if (dist(x, y, cx, cy) > r) continue;
            const cur = this.get(x, y);
            if (cur === T.STONE || cur === T.EBONSTONE || cur === T.ICE || cur === T.SANDSTONE || cur === T.ASH || cur === T.OBSIDIAN)
              this.set(x, y, tile);
          }
      }
    }

    /* --- 5-B. 지층 돌 둘 — 돌(STONE)만 갈아 끼운다. */
    for (const [tile, y0, y1, count, r0, r1] of [[T.LIMESTONE, 0, SY(230), 520, 4, 9], [T.GRANITE, SY(200), HELL_Y - 8, 420, 4, 10]]) {
      const n = Math.round(count * oreScale * WSY);
      for (let k = 0; k < n; k++) {
        const cx = rng.int(4, WW - 5);
        const cy = rng.int(Math.max(y0, this.surface[cx] + 15), y1);
        if (cy >= y1) continue;
        const rx = rng.range(r0, r1), ry = rx * rng.range(0.45, 0.8);   // 가로로 눕힌 덩어리 — 지층처럼
        for (let x = Math.floor(cx - rx); x <= cx + rx; x++)
          for (let y = Math.floor(cy - ry); y <= cy + ry; y++) {
            const dx = (x - cx) / rx, dy = (y - cy) / ry;
            if (dx * dx + dy * dy <= 1 && this.get(x, y) === T.STONE) this.set(x, y, tile);
          }
      }
    }

    /* --- 6. 지옥 용암은 여기서 만들지 않는다 --- */

    // --- 7. 나무 / 덩굴 ---
    /* 나무끼리는 수관 사이에 빈 칸을 둘 이상 둔다(occR = 앞 나무가 차지한 가장 오른쪽 칸).
       정글은 수관이 겹쳐 어두운 게 제맛이라 기둥 사이만 두 칸 띄운다. */
    let occR = -99;
    for (let x = 4; x < WW - 4; x++) {
      const s = this.surface[x];
      if (inSeaZone(x, SEA_X1)) continue;                 // 바다에는 나무가 안 선다
      if (x > vx0 - 6 && x < vx1 + 6) continue;
      if (x > dx0 - 10 && x < dx1 + 10) continue;
      const g = this.get(x, s);
      if (g === T.GRASS && rng.chance(0.14)) { const r = this.tree(x, s, rng, T.WOOD, T.LEAF, occR); if (r !== null) occR = r; }
      else if (g === T.CORRUPTGRASS && rng.chance(0.11)) { const r = this.tree(x, s, rng, T.WOOD, T.CORRUPTLEAF, occR); if (r !== null) occR = r; }
      else if (g === T.SNOW && rng.chance(0.08)) { if (x - 5 >= occR + 3) { this.pineTree(x, s, rng); occR = x + 6; } }
      // 정글은 나무가 빽빽하고 키가 크다 — 수관이 겹쳐 아래가 늘 어둡다
      else if (g === T.JUNGLEGRASS && rng.chance(0.34)) { if (x >= occR + 3) { this.jungleTree(x, s, rng); occR = x + 1; } }
      // 버섯 골짜기는 나무 대신 큰 발광 버섯이 자란다
      else if (g === T.GLOWMOSS && rng.chance(0.2)) this.glowStalk(x, s, rng);
      // 채집물을 흩뿌린다 (나무가 없는 자리에만) — 캐면 제작 재료가 되는 아이템이라 예전 순수 장식 때보다 밀도를 눈에 띄게 올렸다
      else if (g === T.GRASS && this.get(x, s - 1) === T.AIR) {
        if (rng.chance(0.10)) this.set(x, s - 1, T.FLOWER);
        else if (rng.chance(0.18)) this.set(x, s - 1, T.WEED);
      } else if (g === T.SAND && this.get(x, s - 1) === T.AIR) {
        // 사막은 선인장이 주가 되어야 한다 — 잡초는 아주 드물게만.
        if (rng.chance(0.035)) this.cactusPlant(x, s, rng);
        else if (rng.chance(0.16)) this.set(x, s - 1, T.CACTUS);
        else if (rng.chance(0.04)) this.set(x, s - 1, T.WEED);
      } else if ((g === T.CORRUPTGRASS || g === T.SNOW) && this.get(x, s - 1) === T.AIR && rng.chance(0.10)) {
        this.set(x, s - 1, T.MUSHROOM);
      } else if (g === T.JUNGLEGRASS && this.get(x, s - 1) === T.AIR) {
        if (rng.chance(0.10)) this.set(x, s - 1, T.ORCHID);
        else if (rng.chance(0.42)) this.set(x, s - 1, T.FERN);   // 바닥이 고사리로 덮여 있다
      } else if (g === T.GLOWMOSS && this.get(x, s - 1) === T.AIR && rng.chance(0.3)) {
        this.set(x, s - 1, T.GLOWCAP);
      }
    }
    // 동굴 천장 덩굴
    for (let x = 2; x < WW - 2; x++) {
      for (let y = this.surface[x] + 8; y < DEEP_Y; y++) {
        if (this.get(x, y) !== T.AIR || !this.solid(x, y - 1)) continue;
        if (!rng.chance(0.012)) continue;
        for (let k = 0; k < rng.int(2, 6) && this.get(x, y + k) === T.AIR; k++) this.set(x, y + k, T.VINE);
        break;
      }
    }

    // --- 8. 구조물 ---
    this.buildVillage(vx0, vx1, vh, rng);
    this.buildDawnCity(dx0, dx1, dh, rng);
    this.buildSea(rng, n1);        // 세션 3 심해 — 구조물보다 먼저(그 자리를 피해 놓게)
    this.buildWorks(dx0, dx1, rng);
    this.buildRunaway(dx0, dx1, rng);
    this.buildAtelier(rng);
    this.buildDungeon(rng, n2);
    this.buildSkyIslands(rng, n1);
    this.buildRuins(rng);
    this.buildCitadel(rng);
    this.buildDeepShaft(rng);
    this.buildCaverns(rng);
    this.buildTunnels();         // 대공동 · 엇갈린 굴 · 긴 굴 — 제 난수, 물을 채우기 전에
    this.buildRuinCaches(rng);   // 동굴이 생긴 뒤라야 동굴 상자를 놓을 수 있다
    this.floodCaves(rng);
    this.floodHell(rng);
    this.decorateHell();
    this.buildJungleFalls(rng);
    this.buildCaveZones(rng);      // 동굴 갈래 · 장식 · 금 간 자갈 — 물이 고인 뒤라야 바닥을 안다
    this.scatterChests(rng);
    this.buildAltars(rng);
    /* 마지막으로 유적을 **걸어서** 오갈 수 있는지 확인하고 고친다. */
    this.restoreSealRoom();      // 동굴이 헐고 간 봉인실 바닥·문 앞 복도를 되돌린다
    for (const j of this._walkJobs || []) this._ensureWalkable(j[0], j[1], j[2], j[3], j[4], j[5], j[6], rng);
    this.ensureEntranceTraps(rng);   // 함정 없이 그냥 걸어 들어가는 문을 남기지 않는다
    this.breakLongRuns(rng);         // 함정 하나 없이 쭉 걸어가는 직선 구간을 끊는다
    this.sealCipherVaults();         // 암호 골방의 껍질을 한 번 더 세운다
    this.sweepFloatingDecor();       // 뒷공사가 받침을 헐고 간 장식을 걷어낸다
    this.pruneBrokenTrees();         // 천장에 잘리거나 밑동이 날아가 뜬 나무를 통째로 걷는다
    this.clearSkyOverSettlements();  // 캠프 · 여명 마을 위 하늘 섬은 덩어리째 지운다(잘린 섬을 남기지 않게)
    this.sculptGlacier();            // 빙하 — 나무 없는 맨얼음 · 얼음 탑 · 크레바스 · 압력 능선(제 난수)
    this.softenFrost();              // 서리 지대 — 눈 둔덕 · 눈 덮인 바위(제 난수)
    /* 액체 마무리는 **지형을 건드리는 마지막 단계 뒤**에 와야 한다. */
    this.sweepPockets(60);           // 뒷공사가 남긴 한두 칸짜리 구멍을 메운다 — 물을 고치기 전에
    this.sealLiquids();
    this.decorateWater(rng);     // 물 위 초목 정리 + 수련 — 수면 높이가 확정된 뒤라야 한다
    this.decoratePonds(rng);     // 동굴 웅덩이 — 수련·물풀·부들·조약돌, 둘레 이끼
    this.fillMossCorners();      // 바닥 이끼와 벽 이끼가 만나는 오목한 모서리 칸도 이끼로
    this.springFalls();          // 샘 없는 폭포(정글 절벽)에 샘을 단다 — 유체를 켜기 전에
    /* 뒷공사(상자·제단·통행 보수)가 자갈 칸을 덮어쓴 자리는 목록에서 뺀다 — 남겨 두면 아무것도 없는 벽을 캤을 때 무너질 자리를 찾다가 엉뚱한 곳이 열린다 */
    this.faults = (this.faults || []).filter(f => this.get(f.x, f.y) === T.FAULTSTONE);
    this.applyStrata();          // 깊이층 — 남은 돌 · 자연 벽지를 층 돌로(장식 · 물 · 광맥이 '돌'을 본 뒤라야 한다)
    this.placeRichOres();        // 광상 — 제 난수, 광맥 칸만 바꾼다

    this.spawnX = (vx0 + vx1) >> 1;          // 광장 가운데(x0+50)
    this.spawnY = vh - 3;
    this.fitObjects();
    this.settleMemories();       // 석판 유적 기억 조각 — 헐린 바닥 위에 뜨지 않게
    this.placeRigs(true);        // 채취탑 자리 — 물건을 다 맞춘 뒤(지면·유적이 확정된 뒤)
    this.restoreCanopies();      // 뒷공사에 반쯤 깎인 수관을 되채운다 — 기둥이 남은 나무만
    this.pruneBrokenTrees();     // 한 번 더 — 채취탑 · 물가 손질이 남긴 줄기 없는 잎 덩어리(나중 단계라 앞의 정리가 못 봤다)
    this.fluidInit();            // 여기서부터 물이 흐른다 — 생성 중에는 꺼 둔다(set 이 수백만 번 불린다)
    this.fluidSettle();          // 샘에서 폭포·물길이 흘러 자리 잡을 때까지 미리 돌린다
    return this;
  }

  /* ================= 충돌 ================= */
  /** 사각형이 막힌 칸이나 닫힌 문의 판과 겹치는지 */
  hitSolid(px: any, py: any, w: any, h: any) {
    if (super.hitSolid(px, py, w, h)) return true;
    for (const d of this.doors) {
      if (!d.closed) continue;
      const e = this.doorEdge(d);
      if (px < e.x + e.w && px + w > e.x && py < d.y + d.h && py + h > d.y) return true;
    }
    return false;
  }
  doorEdge(d: any) { return doorEdge(d); }
  /** 문 하나를 만들어 objects/doors 양쪽에 같은 참조로 등록한다 (열고 닫는 상태가 항상 같이 반영되도록). */
  pushDoor(x: any, y: any, w: any, h: any, dir: any, extra?: any) {
    const d = Object.assign({ type: 'door', x, y, w, h, closed: true, dir: dir || -1 }, extra);
    this.objects.push(d); this.doors.push(d);
    return d;
  }

  /* ================= 조명 ================= */
  /** 화면 범위 조명 계산 — 두 겹을 따로 퍼뜨려 큰 쪽을 쓴다.
      ① 햇빛·바닷속 빛·약한 빛·액체 빛: 예전처럼 번진다(트인 칸 0.92 · 막힌 칸 2.7씩 줄어듦).
      ② 강한 점 광원(횃불·등불·수정 …, 세기 LIGHT_POINT 이상)과 플레이어 미광: 칸마다 광선을 쏘아 **가려진 칸은 그늘**(castPointLight),
         그다음 튀는 빛만 조금(LIGHT_BOUNCE 씩) 번진다 — 모서리를 돌아 다 밝아지면 그림자가 사라진다.
      ★ 두 겹 다 막힌 칸은 빛을 받되 트인 칸으로 넘기지 않는다(sweepLightGrid op) — 벽 한두 칸 뒤의 빈 방이 밝던 것. 사연: docs/code-history.md#h142 */
  computeLight(tx0: number, ty0: number, tx1: number, ty1: number, dayLight: number, extra: number[][] | null, sun?: { x: number; y: number; k: number } | null, leafKeep?: (t: number) => number, bodies?: number[][]) { const { WW, WH, SKY_Y } = this.dims;
    const P = 14;
    const x0 = clamp(tx0 - P, 0, WW - 1), x1 = clamp(tx1 + P, 0, WW - 1);
    const y0 = clamp(ty0 - P, 0, WH - 1), y1 = clamp(ty1 + P, 0, WH - 1);
    const w = x1 - x0 + 1, h = y1 - y0 + 1;
    const F = this.light = this.light || new LightField({ fall: LIGHT_FALL, tint: LIGHT_TINT,
      blocked: (x, y) => this.lightBlocks(x, y), key: (x, y) => y * WW + x });
    F.begin(x0, y0, w, h);
    const L = F.L, op = F.op, dec = F.dec, decP = F.decP, trans = F.trans;
    const sea = this.sea, seaAmb = 1.2 + dayLight * 0.1;   // 깊은 바다: 낮 2.7 · 밤 1.5 (/15)
    /* 해가 떠 있으면 트인 하늘 칸은 하늘빛(SUN_AMB)만 받고, 해가 곧게 닿는 칸이 나머지를 받는다 — 그늘이 해 반대쪽으로 눕는다 */
    const sunK = sun ? sun.k : 0, skyL = dayLight * (1 - (1 - SUN_AMB) * sunK);
    const pts: number[] = [];
    // 시드
    for (let x = x0; x <= x1; x++) {
      const s = this.surface[x];
      for (let y = y0; y <= y1; y++) {
        const t = this.tiles[y * WW + x];
        const d = TILE_DEF[t];
        const k = (y - y0) * w + (x - x0);
        op[k] = LIGHT_OPAQUE[t];
        // 창문은 고체지만 빛은 거의 그대로 통과한다 — 2층 집 안이 낮에 환해지는 이유 · 바닷물은 빛을 덜 먹는다
        dec[k] = d.clear ? 1.05 : d.solid === 1 ? 2.7 : t === T.SEAWATER ? 0.42 : 0.92;
        decP[k] = Math.max(dec[k], LIGHT_BOUNCE);
        if (d.light) {
          if (d.light >= LIGHT_POINT && !d.liquid) pts.push(x, y);
          else L[k] = Math.max(L[k], d.light);
        }
        /* 햇빛 통과 — 밟을 수 없는 것(나무 기둥 · 잎 · 장식)은 그늘이 옅다(SUN_SOFT). 사연: docs/code-history.md#h146 */
        /* ★ 하늘 섬(SKY_Y 위)은 해를 가리지 않는다 — 섬 서른 개 넘게 땅에 그늘을 떨궈 지면이 얼룩덜룩했다 */
        trans[k] = y < SKY_Y || t === T.AIR ? 1 : d.solid === 1 ? (op[k] ? 0 : 1) : d.liquid ? 0.8
          : d.leaf ? 1 - (1 - SUN_LEAF) * (leafKeep ? leafKeep(t) : 1) : d.tree ? SUN_SOFT : SUN_PLANT;
        if (t === T.AIR && y <= s && this.walls[y * WW + x] === 0) L[k] = Math.max(L[k], skyL);
        /* 바닷물은 깊이만큼 햇빛을 잃되 흩어진 빛이 남는다 — 수면이 화면(+14칸) 밖이면 스윕이
           빛을 못 받아 수심 30칸부터 새까매졌고, 그 어둠에 바다 몹이 통째로 묻혔다. */
        else if (d.sea && sea && y > sea.level)
          L[k] = Math.max(L[k], dayLight - (y - sea.level) * 0.42, seaAmb);
      }
    }
    // 점 광원 — 그늘을 계산해 둔 판(광원 칸마다 저장)을 찍는다. 빛 색도 같은 판으로 모은다(drawGlow)
    for (let i = 0; i < pts.length; i += 2) {
      const d = TILE_DEF[this.tiles[pts[i + 1] * WW + pts[i]]];
      F.stampPoint(pts[i], pts[i + 1], d.light!, LIGHT_RGB[d.lc!] || null);
    }
    // 추가 광원 (플레이어 미광) — 움직이므로 저장하지 않는다
    if (extra) for (const [ex, ey, ev] of extra) if (ex >= x0 && ex <= x1 && ey >= y0 && ey <= y1) F.stampPoint(ex, ey, ev, null, false);
    /* 직사광(engine castSunlight) — 지표 아래로는 SUN_DEPTH 칸에 걸쳐 흐려진다(곧은 굴로 햇빛이 바닥까지 꽂히지 않게) */
    if (sun && sunK > 0) {
      const top = dayLight * sunK + skyL * (1 - sunK);
      F.sun(sun.x, sun.y, (x, y) => y < 0 || y <= this.surface[clamp(x, 0, WW - 1)] ? 1 : 0, (k, raw) => {
        const dd = y0 + ((k / w) | 0) - this.surface[x0 + k % w];
        return top * raw * (dd <= 0 ? 1 : Math.max(0, 1 - dd / SUN_DEPTH));
      }, S => this._soilSun(S, x0, y0, w, h, top, op, L));
    }
    F.finish(2);
    /* 몸(px 상자 [x, y, w, h]) 그늘 — 같은 직사광 엔진으로 번지기 뒤에 진다(덮인 넓이만큼 · 칸 사이를 섞어 미끄러진다). 사연: docs/code-history.md#h194 */
    if (bodies && sun && sunK > 0) {
      const cells: number[] = [];
      for (const [bx, by, bw, bh] of bodies)
        for (let y = Math.floor(by / TS); y <= Math.floor((by + bh - 1) / TS); y++) for (let x = Math.floor(bx / TS); x <= Math.floor((bx + bw - 1) / TS); x++) {
          if (x < x0 || x > x1 || y < y0 || y > y1) continue;
          const cov = (Math.min(bx + bw, (x + 1) * TS) - Math.max(bx, x * TS)) * (Math.min(by + bh, (y + 1) * TS) - Math.max(by, y * TS)) / (TS * TS);
          if (cov > 0) cells.push((y - y0) * w + (x - x0), 1 - (1 - SUN_BODY) * Math.min(1, cov * 1.4));
        }
      F.occlude(cells, 0.55 * sunK, true);
    }
  }
  /** 그늘은 땅 겉면 한 칸에만 떨어진다 — 그 밑 흙은 둘레 ±SOIL_R 칸 겉면의 평균 햇빛에서 칸마다 2.7씩 줄여 채운다.
      ★ 겉면 칸 하나하나에서 번지게 두면 칸마다 2.7 씩 옆으로도 줄어 흙 속에 그늘 모양 삼각형이 박혔다(사용자 지적). 사연: docs/code-history.md#h188 */
  _soilSun(S: Float32Array, x0: number, y0: number, w: number, h: number, top: number, op: Uint8Array, L: Float32Array) {
    const g = new Float32Array(w), gs = new Float32Array(w);
    for (let i = 0; i < w; i++) { const gy = this.surface[x0 + i] - y0; g[i] = gy >= 0 && gy < h ? S[gy * w + i] : 1; }
    for (let i = 0; i < w; i++) {
      let a = 0, n = 0;
      for (let j = Math.max(0, i - SOIL_R); j <= Math.min(w - 1, i + SOIL_R); j++) { a += g[j]; n++; }
      gs[i] = a / n;
    }
    for (let i = 0; i < w; i++) {
      const gy = this.surface[x0 + i] - y0;
      for (let dd = 1; dd <= 6; dd++) {
        const y = gy + dd; if (y < 0 || y >= h) continue;
        const k = y * w + i; if (!op[k]) continue;
        const v = top * (dd === 1 ? (g[i] + gs[i]) / 2 : gs[i]) - 2.7 * dd;   // 겉면 바로 밑 한 칸은 반쯤 이어 그늘 가장자리를 부드럽게
        if (v > L[k]) L[k] = v;
      }
    }
  }
  /** 그 칸이 점 광원의 빛을 막는가(세계 밖은 막힌 것으로) */
  lightBlocks(x: number, y: number) { const { WW, WH } = this.dims;
    return x < 0 || y < 0 || x >= WW || y >= WH || LIGHT_OPAQUE[this.tiles[y * WW + x]] === 1;
  }
  /** 칸이 바뀌었다 — 막힘이나 빛이 달라졌으면 그 둘레 광원 판을 버리게 적어 둔다(was: 예전 타일, -1 이면 모름) */
  lightTouch(k: number, was: number) { const { WW } = this.dims;
    const t = this.tiles[k];
    if (was >= 0 && LIGHT_OPAQUE[was] === LIGHT_OPAQUE[t] && TILE_DEF[was].light === TILE_DEF[t].light) return;
    this.light!.touch(k % WW, (k / WW) | 0);
  }
  /** 빛 색(점 광원) — 0~1 rgb, 화면 밖이면 null */
  lightColAt(x: number, y: number) { return this.light ? this.light.colorAt(x, y) : null; }
  lightAt(x: number, y: number) { return this.light ? this.light.at(x, y) : 0; }

  /* ================= 유체 ================= */
  /** 동굴 호수를 폭포가 떨어지는 열(fx)까지 **한 덩어리로** 잇는다. */
  _extendLake(lk: any, fx: number, dir: number) {
    const x0 = Math.min(fx, lk.x0), x1 = Math.max(fx, lk.x1), top = lk.top;
    for (let x = x0; x <= x1; x++)
      for (let y = top - 4; y <= top + 2; y++) if (this.ruinAt(x, y) || this.get(x, y) === T.BEDROCK) return false;
    const wet = (t: any) => t === T.WATER || t === T.LILY || t === T.PONDWEED;
    for (let x = x0; x <= x1; x++) {
      if (wet(this.get(x, top)) && wet(this.get(x, top + 1))) continue;
      for (let y = top; y <= top + 1; y++) if (!wet(this.get(x, y))) this.set(x, y, T.WATER);
      if (!this.solid(x, top + 2) && !wet(this.get(x, top + 2))) this.set(x, top + 2, T.STONE);
      // 수면 위 둔덕 — 네 칸 안에 빈칸이 있으면 둔덕이다(없으면 벽이라 두고, 굴 지붕 밑 물이 된다)
      let ya = -1;
      for (let y = top - 1; y >= top - 4; y--) if (this.get(x, y) === T.AIR) { ya = y; break; }
      if (ya >= 0) for (let y = ya + 1; y < top; y++) if (this.solid(x, y)) this.set(x, y, T.AIR);
    }
    if (fx < lk.x0 || fx > lk.x1)
      for (let y = top; y <= top + 1; y++) if (!this.solid(fx + dir, y)) this.set(fx + dir, y, T.STONE);
    lk.x0 = x0; lk.x1 = x1;
    return true;
  }
  /** 옛 세이브·생성된 폭포의 윗머리 — 폭포 꼭대기 위가 막혀 있으면 그 칸을 샘 바위로, 위가 트여 있으면(정글 절벽 폭포처럼 땅 위로 쏟아지는 것) 꼭대기 칸을 샘 바위로 바꾼다. */
  /** 폭포 위 물길 — 그 줄을 따라 물이 끝나는 곳의 벽에 샘이 없으면 단다(양을 보존하는 물에서 물길이 마르지 않게 · 옛 세이브도) */
  _feedSpring(k: number) { const { WW } = this.dims;
    for (const s of [-1, 1]) {
      let n = k;
      for (let i = 0; i < 40 && FLUID_KIND[this.tiles[n + s]] === 1; i++) n += s;
      for (const d of [s, -WW + s, WW + s]) if (this.tiles[n + d] === T.SPRING) return;
      const e = n + s;
      if (n !== k && TILE_DEF[this.tiles[e]].solid === 1 && this.tiles[e] !== T.BEDROCK) { this.set(e % WW, (e / WW) | 0, T.SPRING); return; }
    }
  }
  springFalls() { const { WW, WH } = this.dims;
    for (let k = WW; k < WW * (WH - 1); k++) {
      if (this.tiles[k] !== T.FALLS) continue;
      const up = this.tiles[k - WW];
      if (up === T.FALLS || up === T.SPRING) continue;
      if (FLUID_KIND[up]) { this._feedSpring(k - WW); continue; }
      if (up === T.BEDROCK) continue;
      const x = k % WW, y = (k / WW) | 0;
      if (TILE_DEF[up].solid === 1) { this.set(x, y - 1, T.SPRING); continue; }
      /* ★ 위가 트인 폭포 — 옆 두 칸 안에 물(물길 · 호수)이 있으면 그 물이 넘쳐 떨어지는 것이라 샘이 필요 없다.
         예전엔 폭포 꼭대기 칸을 샘 바위로 바꿔 허공에 바위가 떠 있었다(정글 절벽, 사용자 지적) */
      let fed = false;
      for (let dy = -1; dy <= 0 && !fed; dy++) for (let dx = -2; dx <= 2; dx++) { const t = this.get(x + dx, y + dy); if (t !== T.FALLS && FLUID_KIND[t]) { fed = true; break; } }
      if (fed) continue;
      // 옆이 벽이면 그 벽에 샘을 박는다(물이 벽에서 나온다), 아니면 폭포 머리를 그냥 둔다
      for (const dx of [-1, 1]) if (TILE_DEF[this.get(x + dx, y)].solid === 1 && this.get(x + dx, y) !== T.BEDROCK) { this.set(x + dx, y, T.SPRING); break; }
    }
  }

  /* ================= 저장 ================= */
  /** noGrid 이면 타일 · 벽지를 빼고 담는다 — 저장은 그 둘을 바탕 + 바뀐 청크로 따로 싣는다(game/save.ts saveDelta). */
  serialize(noGrid = false) { const { WSIZE, WW, WH } = this.dims;
    return {
      seed: this.seed, ww: WW, wh: WH, size: WSIZE, ruinSites: this.ruinSites, ruinEvents: this.ruinEvents,
      tiles: noGrid ? null : rleEncode(this.tiles),
      walls: noGrid ? null : rleEncode(this.walls),
      surface: Array.from(this.surface),
      objects: this.objects.map(o => ({ ...o })),
      machines: Array.from(this.machines.values()),
      crops: Array.from(this.crops), wet: this.wet || {}, built: Array.from(this.built || []),
      spawnX: this.spawnX, spawnY: this.spawnY, villageY: this.villageY,
      dawnY: this.dawnY, dawnCity: this.dawnCity, works: this.works, runaway: this.runaway,
      atelier: this.atelier, citadel: this.citadel, deepShaft: this.deepShaft,
      dungeon: this.dungeon, ruins: this.ruins, sealRoom: this.sealRoom,
      skyIslands: this.skyIslands, skyGate: this.skyGate, giantTree: this.giantTree,
      caverns: this.caverns, pools: this.pools, lavaPools: this.lavaPools, falls: this.falls, sea: this.sea || null,
      caveGrid: this.caveGrid ? Array.from(this.caveGrid) : null, faults: this.faults || [], oreHits: this.oreHits || {},
      liq: this.liqSave(),           // 덜 찬 물칸의 양(world/fluid.ts) — 원천은 타일이 곧 양
      explored: rleEncode(this.explored)
    };
  }
  static deserialize(d: any) { const { WW, WH } = dimsOf();
    const w = new World(d.seed);
    w.tiles = d.tiles instanceof Uint8Array ? d.tiles : rleDecode(d.tiles, WW * WH, Uint8Array);   // 청크로 푼 세이브는 배열이 바로 온다
    w.walls = d.walls instanceof Uint8Array ? d.walls : rleDecode(d.walls, WW * WH, Uint8Array);
    w.surface = Int16Array.from(d.surface);
    w.objects = d.objects;
    w.fitObjects();   // 규격 도입 전 세이브에 담긴 큰 설치물도 여기서 한 칸 크기로 맞춘다
    /* ★ 이미 연 암호 골방의 문간을 다시 뚫어 본다 — 사연: docs/code-history.md#h136 */
    for (const o of w.objects)
      if (o.type === 'codedoor' && o.opened) w.openCodeDoorway(o.dx, o.dy);
    w.doors = w.objects.filter(o => o.type === 'door');   // objects와 같은 참조로 다시 캐싱
    for (const m of (d.machines || [])) {
      if (m.it) { delete m.it.t0; delete m.it.fx; delete m.it.fy; delete m.it.src; }   // 미끄러짐 시각은 지난 판의 G.time — 남기면 한 칸 뒤에 얼어붙는다
      w.machines.set(m.y * WW + m.x, m);
      for (let dy = 0; dy < (m.h || 1); dy++) for (let dx = 0; dx < (m.w || 1); dx++)
        if (dx || dy) w.mpart.set((m.y - dy) * WW + m.x + dx, m.y * WW + m.x);
    }
    for (const k of (d.crops || [])) w.crops.add(k);
    w.wet = d.wet || {};
    w.built = new Set(d.built || []);
    w.netDirty = true;
    // 예전 세이브(v3 이전)에는 explored가 없다 — 그런 경우 처음부터 다시 밝혀 나가면 된다
    if (d.explored) w.explored = rleDecode(d.explored, WW * WH, Uint8Array);
    w.spawnX = d.spawnX; w.spawnY = d.spawnY; w.villageY = d.villageY;
    w.dawnY = d.dawnY; w.dawnCity = d.dawnCity; w.works = d.works; w.runaway = d.runaway;
    w.atelier = d.atelier || null;   // 설계실이 생기기 전 세이브에는 없다
    w.citadel = d.citadel || null; w.deepShaft = d.deepShaft || null;
    w.dungeon = d.dungeon; w.ruins = d.ruins; w.sealRoom = d.sealRoom; w.ruinSites = d.ruinSites || []; w.ruinEvents = d.ruinEvents || [];
    w.skyIslands = d.skyIslands; w.skyGate = d.skyGate; w.giantTree = d.giantTree;
    // 물이 생기기 전의 세이브에는 이 둘이 없다 — 타일에는 이미 물이 없으니 빈 배열이 맞다
    w.caverns = d.caverns || []; w.pools = d.pools || []; w.lavaPools = d.lavaPools || [];
    w.falls = d.falls || [];   // 폭포 앰비언트 도입 전 세이브 — 빈 배열이면 그냥 조용할 뿐, 안전하다
    /* ★ 바다 수면(sea.level)은 물고기 표·부유물·파도 타기가 읽는다 — 저장이 빠졌던 동안 불러온 세계에서 바다 물고기 생성이 터졌다.
       그 세이브는 타일에서 다시 잰다: 왼쪽 끝 기둥의 첫 바닷물 칸이 수면, 그 줄을 따라 바닷물이 이어지는 끝이 물가. */
    w.sea = d.sea || null;
    if (!w.sea) {
      let lv = -1;
      for (let y = 1; y < WH && lv < 0; y++) if (w.tiles[y * WW + 5] === T.SEAWATER) lv = y;
      if (lv > 0) {
        let x1 = 5;                            // 섬이 수면 줄을 끊으므로 40칸 안의 틈은 건너뛴다
        for (let x = 6, gap = 0; x < WW && gap < 40; x++) {
          if (w.tiles[lv * WW + x] === T.SEAWATER || w.tiles[(lv + 1) * WW + x] === T.SEAWATER) { x1 = x; gap = 0; } else gap++;
        }
        w.sea = { x1, level: lv, floor: null };
      }
    }
    if (w.sea) { w.seaLevel = w.sea.level; w.shoreY = w.sea.level + 2; }
    // 동굴 갈래 도입 전 세이브 — 갈래가 없으면 모든 굴이 plain 이고, 무너질 자갈도 없다
    w.caveGrid = d.caveGrid ? Uint8Array.from(d.caveGrid) : null;
    w.faults = d.faults || [];
    w.oreHits = d.oreHits || {};
    // 유체 — 샘 없는 옛 폭포에 샘을 달아 주고 켠다(springFalls 의 ★)
    w.springFalls();
    w._liqLoad = d.liq || null;
    w.fluidInit();
    return w;
  }
}
